import { formatTableData } from "../utils/formatters";

interface CouncilBluffsData {
  address?: string | null;
  taxAddress?: string | null;
  city?: string | null;
  state?: string | null;
  zip?: string | null;
  lot?: string | null;
  block?: string | null;
  parcelId?: string | null;
  ownerName?: string | null;
  ownerName2?: string | null;
  subdivisionName?: string | null;
  addition?: string | null;
  section?: string | null;
  township?: string | null;
  range?: string | null;
  tract?: string | null;
  legalDescription?: string | null;
  acres?: number | null;
  areaSqFt?: number | null;
  inCity?: string | null;
  jurisdiction?: string | null;
  zoningCode?: string | null;
  floodZone?: string | null;
  firmPanel?: string | null;
  historicPreservationArea?: string | null;
  transitionalDispose?: string | null;
  forgivableMortgage?: string | null;
  totalValue?: string | null;
  totalValue10Pct?: string | null;
  inspectionZoneId?: string | null;
}

const isPresent = (value: unknown): value is string | number => {
  if (value === null || value === undefined) {
    return false;
  }

  if (typeof value === "string") {
    return value.trim() !== "";
  }

  if (typeof value === "number") {
    return !Number.isNaN(value);
  }

  return true;
};

const presentText = (value: string | null | undefined): string => {
  return isPresent(value) ? value.trim() : "";
};

const formatPlss = (value: string | null | undefined): string => {
  if (!isPresent(value)) {
    return "";
  }

  const trimmed = value.trim();
  if (trimmed.toLowerCase() === "no") {
    return "";
  }

  return trimmed;
};

const formatYesNo = (value: string | null | undefined): string => {
  if (!isPresent(value)) {
    return "";
  }

  const normalized = value.trim().toUpperCase();
  if (normalized === "Y") {
    return "Yes";
  }
  if (normalized === "N") {
    return "No";
  }

  return value.trim();
};

const formatAreaSqFt = (value: number | null | undefined): string => {
  if (!isPresent(value)) {
    return "";
  }

  return `${Math.round(value).toLocaleString()} sq ft`;
};

const formatCityStateZip = (data: CouncilBluffsData): string => {
  const parts = [data.city, data.state, data.zip].filter(isPresent);
  return parts.length > 0 ? parts.join(", ") : "";
};

const formatOwnerNames = (data: CouncilBluffsData): string => {
  const owners = [data.ownerName, data.ownerName2].filter(isPresent);
  return owners.length > 0 ? owners.join("; ") : "";
};

const COUNCIL_BLUFFS_ROWS = [
  {
    label: "Address",
    getValue: (data: CouncilBluffsData) => presentText(data.address),
  },
  {
    label: "Tax Address",
    getValue: (data: CouncilBluffsData) => presentText(data.taxAddress),
  },
  {
    label: "City, State, Zip",
    getValue: formatCityStateZip,
  },
  {
    label: "Zone",
    getValue: (data: CouncilBluffsData) => presentText(data.zoningCode),
  },
  {
    label: "Parcel ID",
    getValue: (data: CouncilBluffsData) => presentText(data.parcelId),
  },
  {
    label: "Owner",
    getValue: formatOwnerNames,
  },
  {
    label: "Lot",
    getValue: (data: CouncilBluffsData) => presentText(data.lot),
  },
  {
    label: "Block",
    getValue: (data: CouncilBluffsData) => presentText(data.block),
  },
  {
    label: "Subdivision",
    getValue: (data: CouncilBluffsData) => presentText(data.subdivisionName),
  },
  {
    label: "Addition",
    getValue: (data: CouncilBluffsData) => presentText(data.addition),
  },
  {
    label: "Legal Description",
    getValue: (data: CouncilBluffsData) => presentText(data.legalDescription),
  },
  {
    label: "Section",
    getValue: (data: CouncilBluffsData) => presentText(data.section),
  },
  {
    label: "Township",
    getValue: (data: CouncilBluffsData) => presentText(data.township),
  },
  {
    label: "Range",
    getValue: (data: CouncilBluffsData) => formatPlss(data.range),
  },
  {
    label: "Tract",
    getValue: (data: CouncilBluffsData) => formatPlss(data.tract),
  },
  {
    label: "Property Size",
    getValue: (data: CouncilBluffsData) => (isPresent(data.acres) ? `${data.acres} acres` : ""),
  },
  {
    label: "Area",
    getValue: (data: CouncilBluffsData) => formatAreaSqFt(data.areaSqFt),
  },
  {
    label: "In City",
    getValue: (data: CouncilBluffsData) => formatYesNo(data.inCity),
  },
  {
    label: "Jurisdiction",
    getValue: (data: CouncilBluffsData) => presentText(data.jurisdiction),
  },
  {
    label: "Flood Zone",
    getValue: (data: CouncilBluffsData) => presentText(data.floodZone),
  },
  {
    label: "FIRM Panel",
    getValue: (data: CouncilBluffsData) => presentText(data.firmPanel),
  },
  {
    label: "Historic Preservation Area",
    getValue: (data: CouncilBluffsData) => formatYesNo(data.historicPreservationArea),
  },
  {
    label: "Transitional Dispose",
    getValue: (data: CouncilBluffsData) => formatYesNo(data.transitionalDispose),
  },
  {
    label: "Forgivable Mortgage",
    getValue: (data: CouncilBluffsData) => formatYesNo(data.forgivableMortgage),
  },
  {
    label: "Total Value",
    getValue: (data: CouncilBluffsData) => presentText(data.totalValue),
  },
  {
    label: "Total Value (10%)",
    getValue: (data: CouncilBluffsData) => presentText(data.totalValue10Pct),
  },
  {
    label: "Inspection Zone ID",
    getValue: (data: CouncilBluffsData) => presentText(data.inspectionZoneId),
  },
];

export const formatCouncilBluffsData = (data: CouncilBluffsData): string => {
  return formatTableData(
    data,
    COUNCIL_BLUFFS_ROWS.filter((row) => isPresent(row.getValue(data)))
  );
};

export type { CouncilBluffsData };
